import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { TeamsLinkButton } from '@atlaskit/teams-app-internal-navigation/teams-link-button';
import { token } from '@atlaskit/tokens';

import type { PersonProfileData } from '../../types';

const styles = cssMap({
	container: {
		paddingBottom: token('space.200'),
	},
});

const messages = defineMessages({
	collaboratorFeedbackPrompt: {
		id: 'user-profile-preview.SuggestCollaboratorChanges.collaboratorFeedbackPrompt.ai-non-final',
		defaultMessage: 'Doesn’t look right? Provide feedback and rearrange your collaborators',
		description:
			'Helper text beside the collaborator feedback link in the profile preview collaborators tab, inviting feedback and rearrangement of the displayed collaborators.',
	},
	takeMeThere: {
		id: 'user-profile-preview.SuggestCollaboratorChanges.takeMeThere.ai-non-final',
		defaultMessage: 'Take me there',
		description:
			'Link to suggest changes to the focused user’s collaborators in User Shard Explorer',
	},
});

/** Opens collaborator feedback for the focused profile when enabled. */
export function SuggestCollaboratorChanges({
	profileData,
	orgId,
	requestingUserId,
}: {
	profileData: PersonProfileData;
	orgId?: string;
	requestingUserId?: string;
}): React.JSX.Element | null {
	const intl = useIntl();
	if (
		!fg('kd_user_profile_card_cg_feedback') ||
		!requestingUserId ||
		profileData.accountId !== requestingUserId
	) {
		return null;
	}

	const data = encodeURIComponent(
		JSON.stringify({
			accountId: profileData.accountId,
			email: profileData.email,
			name: profileData.name,
			picture: profileData.avatarUrl,
			nickname: profileData.name,
			jobTitle: profileData.jobTitle,
			organization: profileData.organization,
			department: profileData.department,
			location: profileData.location,
			orgId,
		}),
	);

	return (
		<Inline alignBlock="center" space="space.100" shouldWrap xcss={styles.container}>
			<Text color="color.text.subtle">
				{intl.formatMessage(messages.collaboratorFeedbackPrompt)}
			</Text>
			<TeamsLinkButton
				href={`https://user-shard-explorer.atlassian.com/user?data=${data}`}
				intent="external"
				appearance="default"
				iconAfter={LinkExternalIcon}
			>
				{intl.formatMessage(messages.takeMeThere)}
			</TeamsLinkButton>
		</Inline>
	);
}
